import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { Star, Send, Paperclip, X, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SECTION_MAP: Record<string, string> = {
  "/dashboard": "Dashboard", "/discovery": "Discovery", "/creative-studio": "Creative Studio",
  "/ads": "Ads", "/leads": "Leads", "/commerce-orders": "Orders", "/inventory": "Inventory",
  "/reorder": "Reorder", "/financial": "Financial", "/performance": "Performance",
  "/fulfillment": "Fulfillment", "/global-statistics": "Statistics", "/ai-tools": "AI Tools",
};

function detectPage(pathname: string) {
  return SECTION_MAP[pathname] || pathname.replace("/", "").replace(/-/g, " ") || "General";
}

function getBrowserInfo() {
  const ua = navigator.userAgent;
  const w = window.innerWidth;
  const h = window.innerHeight;
  return `${ua} | ${w}x${h}`;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const page = detectPage(location.pathname);

  const handleSubmit = async () => {
    if (!user || rating === 0) return;
    setSubmitting(true);
    try {
      let screenshotUrl: string | null = null;
      if (screenshot) {
        const ext = screenshot.name.split(".").pop();
        const path = `feedback/${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("exports").upload(path, screenshot);
        if (!upErr) {
          const { data } = supabase.storage.from("exports").getPublicUrl(path);
          screenshotUrl = data?.publicUrl || null;
        }
      }

      const { error } = await supabase.from("user_feedback").insert({
        user_id: user.id,
        page,
        rating,
        message: message.trim() || null,
        screenshot_url: screenshotUrl,
        browser_info: getBrowserInfo(),
      });
      if (error) throw error;

      toast.success("Thank you for your feedback!");
      setRating(0);
      setMessage("");
      setScreenshot(null);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Send Feedback</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Rating */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">How's your experience?</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-6 w-6 transition-colors ${
                      n <= (hoverRating || rating)
                        ? "fill-amber-400 text-amber-400"
                        : "text-muted-foreground/30"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Message (optional)</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind…"
              rows={3}
              maxLength={1000}
            />
          </div>

          {/* Screenshot */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
                <Paperclip className="h-3.5 w-3.5" />
                {screenshot ? screenshot.name : "Attach screenshot"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
              </label>
              {screenshot && (
                <button onClick={() => setScreenshot(null)} className="text-muted-foreground hover:text-destructive">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <span className="text-[10px] text-muted-foreground/60">📍 {page}</span>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full btn-gradient text-primary-foreground"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send Feedback
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
